import { msg } from "@lit/localize";

import { APP_NAME, PORTAL_TITEL } from "../../config/app-config.js";
import {
  ROUTE_ABOUT,
  ROUTE_APP,
  ROUTE_AUSSCHUESSE,
  ROUTE_BEZIRK,
  ROUTE_BEZIRKE,
  ROUTE_BEZIRKSVERTRETUNG,
  ROUTE_BEZIRKSVERTRETUNGEN,
  ROUTE_DEZERNATE,
  ROUTE_DOC_AUFBAU,
  ROUTE_DOC_ENTFERNEN,
  ROUTE_DOC_PATTERNS,
  ROUTE_DOC_PROVIDER,
  ROUTE_DRUCKSACHE_DETAIL,
  ROUTE_ENTSCHEIDUNGEN,
  ROUTE_GREMIEN,
  ROUTE_GREMIUM_DETAIL,
  ROUTE_HOME,
  ROUTE_IMPRINT,
  ROUTE_MEMBERS_DASHBOARD,
  ROUTE_MEMBERS_MERKLISTE,
  ROUTE_MEMBERS_MERKLISTE_DETAIL,
  ROUTE_MEMBERS_PROFILE,
  ROUTE_MEMBERS_SYNC,
  ROUTE_PARTEI_DETAIL,
  ROUTE_PARTEIEN,
  ROUTE_PERSON_DETAIL,
  ROUTE_PRIVACY,
  ROUTE_QUELLEN,
  ROUTE_RAT,
  ROUTE_SITEMAP,
  ROUTE_SITZUNG_DETAIL,
  ROUTE_SITZUNGEN,
  ROUTE_VEEDEL,
  ROUTE_WAHL_DETAIL,
  ROUTE_WAHLEN,
} from "../../config/navigation.js";

/**
 * Der Titel des Dokuments — was im Browser-Tab, im Verlauf und im
 * Suchergebnis steht (App-Backlog 4k: der größte einzelne Hebel für die
 * Sichtbarkeit, und zugleich eine Frage der Zugänglichkeit).
 *
 * Zwei Stufen, EINE Form:
 *
 *   1. Die Shell setzt beim Routenwechsel den Namen der ROUTE („Drucksache")
 *      — sofort, bevor die Seite ihre Daten hat, und für Seiten ohne
 *      Seitenkopf (Person, Doku, Mein Bereich, nicht gefunden).
 *   2. Der Seitenkopf verfeinert aus seiner Brotkrume, sobald sie steht
 *      („Drucksache 3541/2025 · Entscheidungen") — der Gegenstand, ohne
 *      dass eine Seite etwas davon wissen muss: Die Brotkrume kennt ihn
 *      bereits.
 *
 * Feinstes zuerst, der Portalname zuletzt, „·" dazwischen: Ein Tab zeigt
 * nur den Anfang, und ein Suchergebnis kürzt hinten. Höchstens zwei
 * Stationen — „Sürth · Stadtbezirk Rodenkirchen", nicht der ganze Weg.
 */

/** Wie die Teile eines Titels verbunden werden. */
export const TITEL_TRENNER = " · ";

/** Eine Station der Brotkrume, so viel davon, wie der Titel braucht. */
export interface TitelStation {
  name: string;
}

/**
 * Der Name der Route, bevor die Seite ihre Daten hat — je Route ein Begriff
 * (Menü = Thema, Seite = Begriff: hier steht „Drucksache", nicht
 * „Entscheidungen"). Die Startseite hat keinen: Sie heißt wie das Portal.
 */
export function routenName(route: string): string | undefined {
  switch (route) {
    case ROUTE_HOME:
      return undefined;
    case ROUTE_ABOUT:
      return msg("Über das Portal", { id: "app.menu.about" });
    case ROUTE_APP:
      return msg("Als App installieren", { id: "app.install.titel" });
    case ROUTE_IMPRINT:
      return msg("Impressum", { id: "app.menu.imprint" });
    case ROUTE_PRIVACY:
      return msg("Datenschutz", { id: "app.menu.privacy" });
    case ROUTE_SITZUNGEN:
      return msg("Sitzungen", { id: "app.sitzungen.title" });
    case ROUTE_SITZUNG_DETAIL:
      return msg("Sitzung", { id: "app.titel.sitzung" });
    case ROUTE_ENTSCHEIDUNGEN:
      return msg("Entscheidungen", { id: "app.menu.entscheidungen" });
    case ROUTE_DRUCKSACHE_DETAIL:
      return msg("Drucksache", { id: "app.titel.drucksache" });
    case ROUTE_WAHLEN:
      return msg("Wahlen", { id: "app.menu.wahlen" });
    case ROUTE_WAHL_DETAIL:
      return msg("Wahlergebnis", { id: "app.titel.wahlergebnis" });
    case ROUTE_GREMIEN:
      return msg("Gremien", { id: "app.menu.gremien" });
    case ROUTE_GREMIUM_DETAIL:
      return msg("Gremium", { id: "app.titel.gremium" });
    case ROUTE_PERSON_DETAIL:
      return msg("Person", { id: "app.titel.person" });
    case ROUTE_BEZIRKSVERTRETUNGEN:
      return msg("Bezirksvertretungen", { id: "app.menu.bvs" });
    case ROUTE_BEZIRKSVERTRETUNG:
      return msg("Bezirksvertretung", { id: "app.bv.pfad" });
    case ROUTE_AUSSCHUESSE:
      return msg("Ausschüsse und Gremien", { id: "app.start.gremien.ausschuesseGremien" });
    case ROUTE_RAT:
      return msg("Rat", { id: "app.rat.pfad" });
    case ROUTE_DEZERNATE:
      return msg("Verwaltung", { id: "app.dezernate.kennung.seite" });
    case ROUTE_PARTEIEN:
      return msg("Parteien", { id: "app.partei.pfad" });
    case ROUTE_PARTEI_DETAIL:
      return msg("Partei", { id: "app.titel.partei" });
    case ROUTE_BEZIRKE:
      return msg("Bezirke", { id: "app.menu.bezirke" });
    case ROUTE_BEZIRK:
      return msg("Stadtbezirk", { id: "app.titel.stadtbezirk" });
    case ROUTE_VEEDEL:
      return msg("Stadtteil", { id: "app.titel.stadtteil" });
    case ROUTE_QUELLEN:
      return msg("Quellen und Stand", { id: "app.quellen.titel" });
    case ROUTE_SITEMAP:
      return msg("Sitemap", { id: "app.sitemap.pfad" });
    case ROUTE_MEMBERS_DASHBOARD:
      return msg("Mein Bereich", { id: "app.menu.dashboard" });
    case ROUTE_MEMBERS_PROFILE:
      return msg("Profil", { id: "app.menu.profile" });
    case ROUTE_MEMBERS_MERKLISTE:
    case ROUTE_MEMBERS_MERKLISTE_DETAIL:
      return msg("Merkliste", { id: "app.bookmarks.title" });
    case ROUTE_MEMBERS_SYNC:
      return msg("Synchronisation", { id: "app.titel.sync" });
    // TEMPLATE-DOC: statisch deutsch wie ihr Menü.
    case ROUTE_DOC_AUFBAU:
    case ROUTE_DOC_PROVIDER:
    case ROUTE_DOC_PATTERNS:
    case ROUTE_DOC_ENTFERNEN:
      return "Dokumentation";
    default:
      return msg("Seite nicht gefunden", { id: "app.titel.nichtGefunden" });
  }
}

/**
 * Der Dokumenttitel aus Teilen: leere fallen weg, der Portalname steht
 * zuletzt. Ohne Teile ist es die Startseite — die heißt wie das Portal,
 * mit dem Domainnamen dahinter, damit beides gefunden wird.
 */
export function dokumentTitel(teile: ReadonlyArray<string | undefined>): string {
  const gefuellt = teile.filter((t): t is string => !!t && t.trim().length > 0);
  if (gefuellt.length === 0) return `${PORTAL_TITEL}${TITEL_TRENNER}${APP_NAME}`;
  return `${gefuellt.join(TITEL_TRENNER)}${TITEL_TRENNER}${APP_NAME}`;
}

/**
 * Der Titel aus der Brotkrume. Die erste Station ist immer die Startseite
 * und fällt weg; von den übrigen zählen die letzten beiden, feinste zuerst.
 */
export function titelAusPfad(pfad: ReadonlyArray<TitelStation>): string {
  const stationen = pfad.slice(1).map((s) => s.name);
  return dokumentTitel(stationen.slice(-2).reverse());
}

/**
 * Die Beschreibung je Thema — was ein Suchergebnis unter dem Titel zeigt.
 * Nur für die Einstiege; alles andere behält die Beschreibung des Portals
 * aus `index.html`, denn ein erfundener Satz ist schlechter als der
 * allgemeine.
 */
export function routenBeschreibung(route: string): string | undefined {
  switch (route) {
    case ROUTE_BEZIRKE:
      return msg(
        "Die Stadtbezirke und ihre Stadtteile: Zahlen, Wahlergebnisse, Entscheidungen und Termine je Gebiet.",
        { id: "app.beschreibung.bezirke" },
      );
    case ROUTE_WAHLEN:
      return msg(
        "Ergebnisse der Kommunal-, Landtags-, Bundestags- und Europawahlen — für die ganze Stadt, je Bezirk und je Stadtteil.",
        { id: "app.beschreibung.wahlen" },
      );
    case ROUTE_ENTSCHEIDUNGEN:
      return msg(
        "Alle Drucksachen des Rates, der Ausschüsse und der Bezirksvertretungen — durchsuchbar, mit Beratungsweg und Stand.",
        { id: "app.beschreibung.entscheidungen" },
      );
    case ROUTE_GREMIEN:
      return msg(
        "Rat, Ausschüsse, Bezirksvertretungen und Verwaltung: wer worüber entscheidet, mit Besetzung und Sitzungen.",
        { id: "app.beschreibung.gremien" },
      );
    case ROUTE_SITZUNGEN:
      return msg(
        "Alle Sitzungstermine mit Tagesordnung, Einladung und Niederschrift — als Liste und im Kalender.",
        { id: "app.beschreibung.sitzungen" },
      );
    case ROUTE_PARTEIEN:
      return msg(
        "Alle Parteien und Wählergruppen: Wahlergebnisse, Sitze, Besetzung der Gremien und Drucksachen.",
        { id: "app.beschreibung.parteien" },
      );
    default:
      return undefined;
  }
}

/** Die Beschreibung des Portals, wie sie in `index.html` steht — der Rückfall. */
let portalBeschreibung: string | undefined;

/** Schreibt den Titel ins Dokument. Außerhalb des Browsers (Tests) tut es nichts. */
export function schreibeTitel(titel: string): void {
  if (typeof document === "undefined") return;
  document.title = titel;
}

/**
 * Schreibt die Beschreibung ins Dokument. Ohne Wert fällt sie auf die des
 * Portals zurück (aus `index.html`, einmal gelesen, dann gemerkt) — damit
 * eine Themenbeschreibung nicht an der nächsten Seite kleben bleibt.
 */
export function schreibeBeschreibung(beschreibung: string | undefined): void {
  if (typeof document === "undefined") return;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!meta) return;
  portalBeschreibung ??= meta.content;
  meta.content = beschreibung ?? portalBeschreibung;
}
